import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { Login } from './routes/Login'
import { useAuth } from './auth/useAuth'
import { ErrorBoundary, RouteErrorElement } from './components/ErrorBoundary'

// Route-level code splitting. The SPA entry only pulls in Home's
// (fairly light) deps on initial load; the big editor / settings
// surfaces are fetched on demand. Every chunk keeps its own KaTeX,
// Yjs, TipTap graph out of the main bundle.
const Home = lazy(() =>
  import('./routes/Home').then((m) => ({ default: m.Home })),
)
const DocView = lazy(() =>
  import('./routes/DocView').then((m) => ({ default: m.DocView })),
)
const DocRaw = lazy(() =>
  import('./routes/DocRaw').then((m) => ({ default: m.DocRaw })),
)
const Settings = lazy(() =>
  import('./routes/Settings').then((m) => ({ default: m.Settings })),
)

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <div className="font-serif italic text-ink-muted">loading…</div>
    </div>
  )
}

/**
 * Wrap a route that requires a signed-in user. Anonymous visitors
 * land on the Login screen instead.
 *
 * `/d/:docId` deliberately does NOT use this — the doc view handles
 * the (auth | share token) decision itself so public viewer links
 * work without login.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <RouteFallback />
  if (!user) return <Login />
  return <>{children}</>
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RequireAuth>
        <Suspense fallback={<RouteFallback />}>
          <Home />
        </Suspense>
      </RequireAuth>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    // Public-capable: DocView resolves access via share token or
    // session. See useAccess + DocView for the gating logic.
    path: '/d/:docId',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <DocView />
      </Suspense>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    // Bare read-only markdown dump. Escape hatch when the full
    // editor crashes — load the ydoc with a minimal TipTap setup
    // (no fancy extensions) and show the markdown so the user can
    // copy it out.
    path: '/d/:docId/raw',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <DocRaw />
      </Suspense>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/settings',
    element: (
      <RequireAuth>
        <Suspense fallback={<RouteFallback />}>
          <Settings />
        </Suspense>
      </RequireAuth>
    ),
    errorElement: <RouteErrorElement />,
  },
])

function App() {
  // Auth is handled per-route now. Most paths gate via `RequireAuth`,
  // but `/d/:docId` is publicly accessible with a valid viewer share
  // token — that path runs its own access check inside DocView.
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  )
}

export default App
